import { scoreItemIneligibility, type TextFormat } from "@repo/domain";
import { Separator } from "@repo/ui/components/ui/separator";
import { SwitchField } from "@repo/ui/form-fields";
import { useStore } from "@tanstack/react-form";
import { AnswerOptionsEditor } from "./answer-options-editor";
import { EnableWhenEditor } from "./enable-when-editor";
import {
  type BuilderItemType,
  type DraftAnswerOption,
  type DraftEnableWhen,
  type DraftNode,
  type DraftQuestion,
  ITEM_TYPES,
} from "./types";
import { useBuilderForm } from "./use-builder-form-setup";
import type { QuestionnaireBuilderActions } from "./use-questionnaire-builder";
import { allowedChildTypes, isAnswerType } from "./utils/allowed-types";
import { createDefaultItem } from "./utils/defaults";
import { generateLinkId } from "./utils/link-id";
import { getItemAtPath } from "./utils/path-helpers";

// A paragraph is the absence of a format, so the select needs a value of its own for it.
const PARAGRAPH = "Paragraph";
const TEXT_FORMATS = [
  { label: "Paragraph", value: PARAGRAPH },
  { label: "Single line", value: "SingleLine" },
  { label: "Email", value: "Email" },
  { label: "Phone", value: "Phone" },
];

const INTEGER_BOUNDS = [
  { key: "min", label: "Min" },
  { key: "max", label: "Max" },
  { key: "step", label: "Step" },
] as const;

type Props = {
  performance?: boolean;
  item: DraftNode;
  scoringEnabled: boolean;
  path: number[];
  allItems: DraftNode[];
  actions: QuestionnaireBuilderActions;
};

export function ItemEditor({ performance = false, scoringEnabled, item, path, allItems, actions }: Props) {
  const initialType = item.kind === "Question" ? item.value.kind : item.kind;
  const integer = item.kind === "Question" && item.value.kind === "Integer" ? item.value : undefined;
  const form = useBuilderForm({
    defaultValues: {
      text: item.kind === "Display" ? item.text : item.label,
      help: item.kind === "Question" ? (item.help ?? "") : "",
      type: initialType,
      required: item.kind === "Question" ? item.required : false,
      repeats: item.kind === "Question" && item.value.kind === "Choice" ? item.value.cardinality === "Multiple" : false,
      maxLength: item.kind === "Question" && item.value.kind === "Text" ? String(item.value.maxLength ?? "") : "",
      textFormat: item.kind === "Question" && item.value.kind === "Text" ? (item.value.format ?? PARAGRAPH) : PARAGRAPH,
      showTogether: item.kind === "Section" ? Boolean(item.showTogether) : false,
      askYesNoFirst: item.kind === "Question" ? Boolean(item.askYesNoFirst) : false,
      min: String(integer?.min ?? ""),
      max: String(integer?.max ?? ""),
      step: String(integer?.step ?? ""),
    },
    onSubmit: () => {},
  });
  const formType = useStore(form.store, (state) => state.values.type) as BuilderItemType;
  const repeats = useStore(form.store, (state) => state.values.repeats);
  const parentPath = path.slice(0, -1);
  const typeOptions = ITEM_TYPES.filter(
    (option) => !performance || !["RepeatingGroup", "Attachment", "Url"].includes(option.value),
  )
    .filter((option) =>
      allowedChildTypes(parentPath.length ? getItemAtPath(allItems, parentPath) : undefined).includes(option.value),
    )
    .map(({ label, value }) => ({ label, value }));
  const isQuestion = isAnswerType(formType);
  const isChoice = formType === "Choice";
  const conditionsAllowed = !performance && (formType === "Section" || isQuestion);

  const update = (updates: Partial<DraftNode>) => actions.updateItem(path, updates);
  const options = item.kind === "Question" && item.value.kind === "Choice" ? item.value.options : [];
  const conditions = "conditions" in item ? (item.conditions ?? []) : [];
  const behavior = "conditionBehavior" in item ? (item.conditionBehavior ?? "all") : "all";
  const hasPoints = isChoice && options.some((option) => option.points !== undefined);
  const section = allItems[path[0]!];
  const pointsDisabledReason = scoreItemIneligibility(
    {
      kind: "Question",
      required: true,
      conditions,
      value: {
        kind: "Choice",
        cardinality: repeats ? "Multiple" : "Single",
        allowFreeText: item.kind === "Question" && item.value.kind === "Choice" ? item.value.allowFreeText : false,
        options: [],
      },
    },
    section && "conditions" in section ? section : {},
    path.length > 2,
  );
  const updateOptions = (next: DraftAnswerOption[]) => {
    if (next.some((option) => option.points !== undefined)) form.setFieldValue("required", true);
    if (item.kind === "Question" && item.value.kind === "Choice") update({ value: { ...item.value, options: next } });
  };
  const updateConditions = (next: DraftEnableWhen[]) => update({ conditions: next } as Partial<DraftNode>);

  return (
    <div className="flex-1 overflow-auto px-4 pb-4 space-y-4">
      {typeOptions.length > 1 && (
        <form.AppField
          name="type"
          listeners={{
            onChange: ({ value }) => {
              if (!value) return;
              const replacement = createDefaultItem(value as BuilderItemType);
              replacement.clientId = item.clientId;
              replacement.id = item.id;
              actions.updateItem(path, replacement);
            },
          }}
        >
          {(field) => <field.SelectField label="Type" options={typeOptions} />}
        </form.AppField>
      )}
      <form.AppField
        name="text"
        listeners={{
          onChangeDebounceMs: 400,
          onChange: ({ value }) => update(item.kind === "Display" ? { text: value } : { label: value }),
        }}
      >
        {(field) => <field.TextareaField label={formType === "Display" ? "Display Text" : "Label"} />}
      </form.AppField>
      {isQuestion && (
        <form.AppField
          name="help"
          listeners={{
            onChangeDebounceMs: 400,
            onChange: ({ value }) => update({ help: value || undefined } as Partial<DraftNode>),
          }}
        >
          {(field) => <field.TextareaField label="Question help" />}
        </form.AppField>
      )}
      {formType === "Section" && (
        <form.AppField
          name="showTogether"
          listeners={{ onChange: ({ value }) => update({ showTogether: value || undefined } as Partial<DraftNode>) }}
        >
          {(field) => <field.SwitchField label="Show questions together on one screen" />}
        </form.AppField>
      )}
      {!performance && item.kind === "Section" && yesNoCandidates(item.items, path).length > 0 && (
        <SwitchField
          label="Ask Yes/No first for all questions"
          description="Sets every single-select choice question in this section"
          value={yesNoCandidates(item.items, path).every(({ question }) => question.askYesNoFirst)}
          onBlur={() => {}}
          onChange={(value) => {
            for (const candidate of yesNoCandidates(item.items, path))
              actions.updateItem(candidate.path, { askYesNoFirst: value || undefined } as Partial<DraftNode>);
          }}
        />
      )}
      {isQuestion && (
        <form.AppField name="required" listeners={{ onChange: ({ value }) => update({ required: value }) }}>
          {(field) => <field.SwitchField label="Required" disabled={performance || hasPoints} />}
        </form.AppField>
      )}
      {isChoice && !performance && (
        <form.AppField
          name="repeats"
          listeners={{
            onChange: ({ value }) =>
              item.kind === "Question" &&
              item.value.kind === "Choice" &&
              update({ value: { ...item.value, cardinality: value ? "Multiple" : "Single" } }),
          }}
        >
          {(field) => <field.SwitchField label="Allow multiple selections" />}
        </form.AppField>
      )}
      {isChoice && !performance && !repeats && (
        <form.AppField
          name="askYesNoFirst"
          listeners={{ onChange: ({ value }) => update({ askYesNoFirst: value || undefined } as Partial<DraftNode>) }}
        >
          {(field) => (
            <field.SwitchField
              label="Ask Yes/No first"
              description="No answers with the lowest-points option; Yes shows the rest"
              disabled={options.length < 2}
            />
          )}
        </form.AppField>
      )}
      {formType === "Integer" && (
        <div className="grid grid-cols-3 gap-3">
          {INTEGER_BOUNDS.map(({ key, label }) => (
            <form.AppField
              key={key}
              name={key}
              listeners={{
                onChangeDebounceMs: 400,
                onChange: ({ value }) => {
                  if (item.kind !== "Question" || item.value.kind !== "Integer") return;
                  const { [key]: _previous, ...rest } = item.value;
                  update({ value: value ? { ...rest, [key]: Number(value) } : rest });
                },
              }}
            >
              {(field) => <field.StringNumberField label={label} step={1} />}
            </form.AppField>
          ))}
        </div>
      )}
      {formType === "Text" && (
        <form.AppField
          name="textFormat"
          listeners={{
            onChange: ({ value }) => {
              if (item.kind !== "Question" || item.value.kind !== "Text") return;
              const { format: _previous, ...rest } = item.value;
              update({ value: value === PARAGRAPH ? rest : { ...rest, format: value as TextFormat } });
            },
          }}
        >
          {(field) => <field.SelectField label="Format" options={TEXT_FORMATS} />}
        </form.AppField>
      )}
      {formType === "Text" && (
        <form.AppField
          name="maxLength"
          listeners={{
            onChangeDebounceMs: 400,
            onChange: ({ value }) =>
              item.kind === "Question" &&
              item.value.kind === "Text" &&
              update({ value: { ...item.value, maxLength: value ? Number(value) : undefined } }),
          }}
        >
          {(field) => <field.StringNumberField label="Max Length" step={1} />}
        </form.AppField>
      )}
      {isChoice && (
        <>
          <Separator />
          <AnswerOptionsEditor
            scoringEnabled={scoringEnabled}
            pointsDisabledReason={pointsDisabledReason}
            options={[...options]}
            onAddOption={() => updateOptions([...options, { clientId: generateLinkId(), label: "" }])}
            onRemoveOption={(index) => updateOptions(options.filter((_, candidate) => candidate !== index))}
            onUpdateOption={(index, option) =>
              updateOptions(options.map((current, candidate) => (candidate === index ? option : current)))
            }
            onMoveOption={(from, to) => {
              const next = [...options];
              next.splice(to, 0, ...next.splice(from, 1));
              updateOptions(next);
            }}
          />
        </>
      )}
      {conditionsAllowed && (
        <>
          <Separator />
          <EnableWhenEditor
            conditions={[...conditions]}
            enableBehavior={behavior}
            excludeClientId={item.clientId}
            allItems={allItems}
            onAddCondition={(condition) => updateConditions([...conditions, condition])}
            onRemoveCondition={(index) => updateConditions(conditions.filter((_, candidate) => candidate !== index))}
            onUpdateCondition={(index, condition) =>
              updateConditions(conditions.map((current, candidate) => (candidate === index ? condition : current)))
            }
            onChangeBehavior={(next) => update({ conditionBehavior: next } as Partial<DraftNode>)}
          />
        </>
      )}
    </div>
  );
}

function yesNoCandidates(items: readonly DraftNode[], at: number[]): { question: DraftQuestion; path: number[] }[] {
  return items.flatMap((node, index) => {
    const path = [...at, index];
    if (node.kind === "RepeatingGroup") return yesNoCandidates(node.items, path);
    return node.kind === "Question" &&
      node.value.kind === "Choice" &&
      node.value.cardinality === "Single" &&
      node.value.options.length > 1
      ? [{ question: node, path }]
      : [];
  });
}
